import * as z from 'zod/mini'
import { keyedIssue, refineWithKey } from '../errors/validation-keys'
import { openingTimeToMinutes, overlappingIndexes } from '../business/misc/opening-hours'
import { isEmbeddableUrl } from '../utils/web-origin'
import { emailField, ianaTimeZone } from './common'

/** Max length of a premise's short description, the one line under its name on the public page. */
export const SHORT_DESCRIPTION_MAX = 140

/** Max number of embedding origins a premise may whitelist. A generous cap, just a sanity bound. */
export const MAX_ALLOWED_ORIGINS = 20

/**
 * A website address a premise may embed its public elements from: an http(s) URL on a real host
 * (`isEmbeddableUrl` - no wildcard, no credentials). The backend reduces each to its origin
 * (`uniqueOrigins`) before saving, since that is the form the browser sends as the CORS `Origin` header.
 */
export const allowedOrigin = z
  .string()
  .check(z.trim(), z.maxLength(255), refineWithKey('validation.httpUrl', isEmbeddableUrl))

/**
 * Field rules for a premise (`EntityEntity`), shared by the forms, the HTTP bodies and the domain
 * operation. The contact email may be left empty - a premise does not have to publish one.
 */
export const entityFields = {
  name: z.string().check(z.trim(), z.minLength(2), z.maxLength(40)),
  description: z.string().check(z.maxLength(50000)),
  /** The line under the name in the public page header. Plain text, one line's worth. */
  shortDescription: z.string().check(z.trim(), z.maxLength(SHORT_DESCRIPTION_MAX)),
  phone: z.string().check(z.trim(), z.maxLength(50)),
  email: z.union([emailField, z.literal('')]),
  timezone: ianaTimeZone,
  allowedOrigins: z.array(allowedOrigin).check(z.maxLength(MAX_ALLOWED_ORIGINS)),
}

/** One opening time. The hour may have one digit (`9:30`): older rows and the seeds store it so. */
const openingTime = z
  .string()
  .check(refineWithKey('validation.timeFormat', (value: string) => openingTimeToMinutes(value) !== null))

/**
 * One opening interval, within a single day: it ends after it starts, so an interval never crosses
 * midnight. A time that is not a time is left to `openingTime` rather than reported twice.
 */
export const openingHour = z.object({ start: openingTime, end: openingTime }).check(
  z.superRefine((value, ctx) => {
    const start = openingTimeToMinutes(value.start)
    const end = openingTimeToMinutes(value.end)
    if (start !== null && end !== null && end <= start) {
      ctx.addIssue(keyedIssue('validation.endAfterStart', ['end']))
    }
  }),
)

/** One day's intervals. They may touch (`08:00–11:00`, `11:00–14:00`) but not overlap. */
export const openingDay = z.array(openingHour).check(
  z.superRefine((hours, ctx) => {
    for (const index of overlappingIndexes(hours)) {
      ctx.addIssue(keyedIssue('validation.openingHoursOverlap', [index, 'start']))
    }
  }),
)

/** Opening hours per ISO weekday (`"1"` Monday … `"7"` Sunday, as JSON object keys). */
export const openingHours = z.partialRecord(z.enum(['1', '2', '3', '4', '5', '6', '7']), openingDay)
